import { Fragment, useState } from "react";
import { styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChatBubble, ChevronRight, CrossLarge } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useChatHistories } from "@acto/taskpane/core/data-access";
import { useChatPersistenceContext, type CustomerContext } from "@acto/taskpane/core/chat";
import type { ChatHistory } from "@acto/taskpane/core/domain";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { useBodyScrollLock } from "@acto/taskpane/field-sales-2/components/primitives/useBodyScrollLock";
import { EmptyState, EmptySubtitle, EmptyTitle } from "@acto/taskpane/field-sales-2/components/primitives/EmptyState";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import {
  Card,
  HoverRowItem,
  HoverRowList,
  InsetRowDivider,
  RowBody,
  RowChevron,
  RowMeta,
  RowSkeletonList,
  RowTitle,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { HeaderRoot } from "@acto/taskpane/field-sales-2/pages/chat/ChatHeader";
import { formatRelativeChatDate } from "./formatRelativeChatDate";
import { HistoryOverlay, ReadOnlyChatViewer } from "./ReadOnlyChatViewer";

const Nav = styled(HeaderRoot)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[10],
}));

const OverlayTitlePill = styled(Pill)(({ theme }) => ({
  boxShadow: theme.fieldSalesTwo.shadows.glass,
}));

const OverlayTitle = styled(Dialog.Title)(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
}));

const IconChip = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  width: 56,
  height: 52,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19,19,19,0.04)",
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": { width: 20, height: 20 },
}));

const HistoryTitle = styled(RowTitle)({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const Sentinel = styled("div")({ height: 1 });

interface ChatHistoryViewProps {
  customer: CustomerContext;
  onClose: () => void;
  onStartNewChat: (message: string) => void;
}

export function ChatHistoryView({ customer, onClose, onStartNewChat }: ChatHistoryViewProps) {
  const tChat = useTranslations("chat");
  const tCommon = useTranslations("common");
  const { locale } = useLocale();
  const [selected, setSelected] = useState<ChatHistory | null>(null);

  useBodyScrollLock(true);

  const {
    data: chatHistories,
    isPending,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
  } = useChatHistories({ customerId: customer.id });

  // The live conversation is persisted as soon as its first reply lands, so it comes back in this
  // list. Showing it would offer the user a read-only copy of the chat they are still in.
  const { chatHistoryId: liveChatHistoryId } = useChatPersistenceContext();
  const pastChats = chatHistories?.filter((history) => history.id !== liveChatHistoryId);

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore: hasNextPage,
    isLoadingMore: isFetching,
    loadMore: fetchNextPage,
    resetKey: pastChats?.length,
  });

  if (selected) {
    return (
      <ReadOnlyChatViewer
        customer={customer}
        chatHistory={selected}
        onClose={() => setSelected(null)}
        onStartNewChat={onStartNewChat}
      />
    );
  }

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <HistoryOverlay aria-describedby={undefined}>
          <Nav>
            <HeaderIconButton type="button" aria-label={tCommon("closeChat")} onClick={onClose}>
              <CrossLarge />
            </HeaderIconButton>
            <OverlayTitlePill variant="action">
              <OverlayTitle>{tChat("chatHistory")}</OverlayTitle>
            </OverlayTitlePill>
          </Nav>

          <Body>
            {isPending ? (
              <Card>
                <RowSkeletonList />
              </Card>
            ) : error ? (
              <EmptyState>
                <ChatBubble aria-hidden="true" />
                <EmptyTitle>{tChat("errorLoadingChatHistory")}</EmptyTitle>
              </EmptyState>
            ) : !pastChats || pastChats.length === 0 ? (
              <EmptyState>
                <ChatBubble aria-hidden="true" />
                <EmptyTitle>{tChat("noChatHistory")}</EmptyTitle>
                <EmptySubtitle>{tChat("noChatHistoryDescription")}</EmptySubtitle>
              </EmptyState>
            ) : (
              <Card>
                <HoverRowList>
                  {pastChats.map((history, index) => (
                    <Fragment key={history.id}>
                      <HoverRowItem clickable onClick={() => setSelected(history)}>
                        <IconChip>
                          <ChatBubble aria-hidden="true" />
                        </IconChip>
                        <RowBody>
                          <HistoryTitle>{history.getPreviewText()}</HistoryTitle>
                          <RowMeta>{formatRelativeChatDate(history.createdAt, tCommon, locale)}</RowMeta>
                        </RowBody>
                        <RowChevron>
                          <ChevronRight />
                        </RowChevron>
                      </HoverRowItem>
                      {index < pastChats.length - 1 && <InsetRowDivider />}
                    </Fragment>
                  ))}
                </HoverRowList>
                {hasNextPage && <Sentinel ref={sentinelRef} aria-hidden="true" />}
              </Card>
            )}
          </Body>
        </HistoryOverlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
